import React, {useEffect, useMemo, useRef, useState} from 'react'
import {UseWindowSize} from '../../features/hooks/UseWindowSize'
import {Link} from 'react-router-dom'
import {PATH_REVIEWS} from '../../routing/paths'
import {ReviewCardDashboard, ReviewStars} from '../../@components'
import {DashboardReviewsGoogle, ReviewItem, ReviewsOutput} from '../../@types'

const output: ReviewsOutput = {
  status: 200,
  warning: false,
  summary: {
    total_count: 5,
    average_rating: 5,
  },
  filters: {
    rating: 0,
    sort: 'recent',
  },
  data: [
    {
      id: 4,
      client_id: '1',
      author_name: 'Oussama Elmaaroufy',
      rating: '5',
      comment:
        'Impeccable service! A serious and responsive team that knows how to provide suitable solutions. Very satisfied with the result, I recommend without hesitation.',
      review_date: '2025-10-08T09:53:07.000000Z',
      external_id: '35b4819bff71f97438bf6fb97ac03641',
      created_at: '2026-03-27T13:23:32.000000Z',
      updated_at: '2026-03-27T13:23:32.000000Z',
    },
    {
      id: 3,
      client_id: '1',
      author_name: 'Hajar El',
      rating: '5',
      comment:
        'Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ab accusamus eos error esse et, exercitationem ipsum libero perspiciatis quia reprehenderit, veniam voluptates? Aliquid asperiores commodi culpa, doloribus earum repellat sequi. Dolor dolores harum maxime porro quasi quia repellendus vel. Adipisci assumenda deleniti dignissimos ducimus error et excepturi expedita fugit id iusto magnam necessitatibus nemo, quaerat quasi rerum! Aperiam atque blanditiis commodi consequatur consequuntur cum cupiditate eligendi facere fugit nemo officia temporibus, vel velit vitae voluptatem. Cumque deserunt dicta placeat! Accusantium debitis, dignissimos distinctio eligendi non quasi unde voluptatum. Adipisci blanditiis corporis id laboriosam laudantium libero nisi perferendis placeat similique sit.',
      review_date: '2025-10-01T18:59:14.000000Z',
      external_id: '3be0bd0c7c031f5db4616a645d8ee527',
      created_at: '2026-03-27T13:23:31.000000Z',
      updated_at: '2026-03-27T13:23:31.000000Z',
    },
    {
      id: 2,
      client_id: '1',
      author_name: 'Kenza SADRY',
      rating: '5',
      comment:
        'A versatile and professional team, who know how to manage complex projects (WordPress, PrestaShop, custom sites) and adapt easily to international clients and projects.',
      review_date: '2025-10-01T14:42:04.000000Z',
      external_id: '52994201d0f7805ab2025fff0be6a04d',
      created_at: '2026-03-27T13:23:30.000000Z',
      updated_at: '2026-03-27T13:23:30.000000Z',
    },
    {
      id: 5,
      client_id: '1',
      author_name: 'Matthieu Langlois',
      rating: '5',
      comment:
        'Euroweb Digital is a trusted partner. They have an experienced, highly responsive, and dedicated team dedicated to creating your showcase or e-commerce websites. I highly recommend them.',
      review_date: '2025-07-25T13:25:11.000000Z',
      external_id: '79bd5f7d0f842c71fa784f1782f85c43',
      created_at: '2026-03-27T13:23:34.000000Z',
      updated_at: '2026-03-27T13:23:34.000000Z',
    },
    {
      id: 1,
      client_id: '1',
      author_name: 'Philippe Coussemaeker (eMax Digital)',
      rating: '5',
      comment:
        'We have worked with Euroweb Digital since its inception for the creation of websites using WordPress, Prestashop, and custom solutions for our French clients. Their team demonstrates great graphic creativity, reliability, and a genuine capacity for innovation.',
      review_date: '2025-07-25T12:16:00.000000Z',
      external_id: 'a67d1ff1da4d52062ca27be98bf9d65c',
      created_at: '2026-03-27T13:23:27.000000Z',
      updated_at: '2026-03-27T13:23:27.000000Z',
    },
  ],
}

const SLIDE_GAP = 22
const COLLAPSED_COMMENT_LENGTH = 200
const COLLAPSED_CARD_HEIGHT = 300

type Props = {
  data?: DashboardReviewsGoogle
}

const ReviewsSlider = ({data}: Props) => {
  const {width} = UseWindowSize()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [expandedReviews, setExpandedReviews] = useState<Record<number, boolean>>({})
  const [expandedCardHeights, setExpandedCardHeights] = useState<Record<number, number>>({})
  const cardContentRefs = useRef<Record<number, HTMLDivElement | null>>({})

  const reviewsData = useMemo<ReviewsOutput>(() => {
    if (!data) {
      return output
    }

    return {
      status: 200,
      warning: false,
      summary: {
        total_count: data.ratings_google?.user_ratings_total ?? data.last_reviews_google?.length ?? 0,
        average_rating: Number(data.ratings_google?.rating ?? 0),
      },
      filters: {
        rating: 0,
        sort: 'recent',
      },
      data: (data.last_reviews_google ?? []).map((item, index) => ({
        id: index + 1,
        client_id: item.client_id,
        author_name: item.author_name,
        rating: item.rating,
        comment: item.comment,
        review_date: item.review_date,
        external_id: item.external_id,
        created_at: item.review_date,
        updated_at: item.review_date,
      })),
    }
  }, [data])

  const hasReviews = reviewsData.data.length > 0
  const visibleCards = width >= 1200 ? 2 : 1
  const maxIndex = Math.max(0, reviewsData.data.length - visibleCards)

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex))
  }, [maxIndex])

  useEffect(() => {
    const nextHeights: Record<number, number> = {}

    reviewsData.data.forEach(({id}) => {
      const content = cardContentRefs.current[id]
      if (content) {
        nextHeights[id] = content.scrollHeight
      }
    })

    setExpandedCardHeights(nextHeights)
  }, [expandedReviews, reviewsData, width])

  const cardBasis = useMemo(
    () => `calc((100% - ${(visibleCards - 1) * SLIDE_GAP}px) / ${visibleCards})`,
    [visibleCards],
  )

  const translateX = useMemo(
    () => `calc(-${currentIndex} * (${cardBasis} + ${SLIDE_GAP}px))`,
    [cardBasis, currentIndex],
  )

  const goPrev = () => setCurrentIndex((prev) => Math.max(0, prev - 1))
  const goNext = () => setCurrentIndex((prev) => Math.min(maxIndex, prev + 1))
  const toggleReview = (id: number) =>
    setExpandedReviews((prev) => ({...prev, [id]: !prev[id]}))
  const getCollapsedComment = (value: string) =>
    value.length > COLLAPSED_COMMENT_LENGTH
      ? `${value.slice(0, COLLAPSED_COMMENT_LENGTH).trimEnd()}...`
      : value

  return (
    <div className="card bg-light-warning h-lg-100">
      <div
        className="card-body p-5 p-md-7"
      >
        <div className="row g-8 align-items-center">
          <div className="col-xl-3 col-lg-4">
            <div className="d-flex flex-column align-items-center justify-content-center h-100">
              <div className="fs-2hx fw-bolder text-gray-900 mb-4">
                EXCELLENT
              </div>
              <div className="mb-5">
                <ReviewStars
                  rating={reviewsData.summary.average_rating}
                  iconClassName="fs-2"
                  wrapperClassName="d-flex align-items-center gap-2"
                />
              </div>
              <div className="text-dark fs-3 fw-normal mb-7">
                Basé sur{' '}
                <span className="fw-bolder text-decoration-underline">{reviewsData.summary.total_count} avis</span>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="text-center">
                  <Link to={PATH_REVIEWS.root} className="btn btn-sm btn-primary me-2 px-6">Voir plus</Link>
                </div>
              </div>
              {/*<div className='d-flex align-items-center gap-3'>*/}
              {/*  <div*/}
              {/*    className='d-flex align-items-center justify-content-center'*/}
              {/*    style={{*/}
              {/*      width: 32,*/}
              {/*      height: 32,*/}
              {/*      borderRadius: '50%',*/}
              {/*      background: '#1f9d73',*/}
              {/*    }}*/}
              {/*  >*/}
              {/*    <i className='bi bi-check-lg text-white fs-4' />*/}
              {/*  </div>*/}
              {/*  <div className='text-gray-800 fw-bolder' style={{fontSize: width >= 768 ? 26 : 22}}>*/}
              {/*    Trustindex*/}
              {/*  </div>*/}
              {/*</div>*/}
            </div>
          </div>

          <div className="col-xl-9 col-lg-8">
            <div className="position-relative">
              {hasReviews ? (
                <>
                  <div
                    className={`page-item previous position-absolute top-50 start-0 translate-middle z-index-2 ${currentIndex === 0 ? 'disabled' : ''}`}
                    style={{marginLeft: -14}}
                  >
                    <button
                      type="button"
                      className={`page-link bg-primary border-primary ${currentIndex === 0 ? 'invisible' : ''}`}
                      onClick={goPrev}
                      disabled={currentIndex === 0}
                      aria-label="Previous reviews"
                    >
                      <i className="previous" style={{filter: 'brightness(0) invert(1)'}} />
                    </button>
                  </div>

                  <div
                    className={`page-item next position-absolute top-50 end-0 translate-middle z-index-2 ${currentIndex >= maxIndex ? 'disabled' : ''}`}
                    style={{marginRight: -44}}
                  >
                    <button
                      type="button"
                      className={`page-link bg-primary border-primary ${currentIndex === maxIndex ? 'invisible' : ''}`}
                      onClick={goNext}
                      disabled={currentIndex >= maxIndex}
                      aria-label="Next reviews"
                    >
                      <i className="next" style={{filter: 'brightness(0) invert(1)'}} />
                    </button>
                  </div>

                  <div className="overflow-hidden">
                    <div
                      className="d-flex"
                      style={{
                        gap: SLIDE_GAP,
                        transform: `translateX(${translateX})`,
                        transition: 'transform 650ms cubic-bezier(0.22, 1, 0.36, 1)',
                        willChange: 'transform',
                      }}
                    >
                      {reviewsData.data.map((item: ReviewItem, index) => {
                        const isExpanded = !!expandedReviews[item.id]
                        const isFocused = index >= currentIndex && index < currentIndex + visibleCards

                        return (
                          <div
                            key={item.id}
                            style={{
                              flex: `0 0 ${cardBasis}`,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                transform: `scale(${isFocused ? 1 : 1.2})`,
                                transition: 'transform 650ms cubic-bezier(0.22, 1, 0.36, 1)',
                                transformOrigin: 'center center',
                              }}
                            >
                              <ReviewCardDashboard
                                item={item}
                                collapsedHeight={COLLAPSED_CARD_HEIGHT}
                                expandedHeight={expandedCardHeights[item.id] ?? COLLAPSED_CARD_HEIGHT}
                                collapsedComment={getCollapsedComment(item.comment)}
                                isExpanded={isExpanded}
                                onToggle={() => toggleReview(item.id)}
                                contentRef={(element) => {
                                  cardContentRefs.current[item.id] = element
                                }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="d-flex justify-content-center gap-3 mt-5">
                    {Array.from({length: maxIndex + 1}, (_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setCurrentIndex(index)}
                        aria-label={`Go to review slide ${index + 1}`}
                        style={{
                          width: index === currentIndex ? 26 : 10,
                          height: 10,
                          borderRadius: 999,
                          background: index === currentIndex ? '#009df5' : 'rgba(0,156,243,.2)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          border: 'none',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="row g-5">
                  {Array.from({length: 2}, (_, index) => (
                    <div key={index} className="col-12 col-md-6">
                      <div
                        className="bg-white border h-100"
                        style={{
                          borderRadius: 18,
                          minHeight: COLLAPSED_CARD_HEIGHT,
                          padding: 24,
                          borderColor: '#ece9e2',
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-6">
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                background: '#f1f1f4',
                                flexShrink: 0,
                                marginRight: 16,
                              }}
                            />
                            <div className="w-100">
                              <div
                                style={{
                                  width: 180,
                                  maxWidth: '100%',
                                  height: 16,
                                  borderRadius: 999,
                                  background: '#f1f1f4',
                                  marginBottom: 10,
                                }}
                              />
                              <div
                                style={{
                                  width: 110,
                                  maxWidth: '70%',
                                  height: 12,
                                  borderRadius: 999,
                                  background: '#f1f1f4',
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 my-4">
                          {Array.from({length: 5}, (_, starIndex) => (
                            <div
                              key={starIndex}
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: 6,
                                background: '#f1f1f4',
                              }}
                            />
                          ))}
                        </div>

                        <div className="mb-8">
                          <div
                            style={{
                              width: '100%',
                              height: 14,
                              borderRadius: 999,
                              background: '#f1f1f4',
                              marginBottom: 12,
                            }}
                          />
                          <div
                            style={{
                              width: '92%',
                              height: 14,
                              borderRadius: 999,
                              background: '#f1f1f4',
                              marginBottom: 12,
                            }}
                          />
                          <div
                            style={{
                              width: '84%',
                              height: 14,
                              borderRadius: 999,
                              background: '#f1f1f4',
                            }}
                          />
                        </div>

                        <div
                          style={{
                            width: 95,
                            height: 14,
                            borderRadius: 999,
                            background: '#f1f1f4',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {ReviewsSlider}
